import { Injectable } from '@angular/core';
import { map, Observable, of, from, firstValueFrom } from 'rxjs';

import { liveQuery } from 'dexie';
import { alert } from 'devextreme/ui/dialog';

import {Constructionsite} from '../models/constructionsite'
import {Cost} from '../models/cost'
import {HoursOption} from '../models/hours-option'
import {KindOfWork} from '../models/kind-of-work'
import {Tool} from '../models/tool'
import {WorkerCost} from '../models/worker-cost'
import {Worker} from '../models/worker'
import {db} from '../models/db'
import { Registration, ResourceRegistration, ControlRegistration, SelectedToolRegistration, SelectedWorkerRegistration, ToolRegistration, WorkerRegistration } from '../models/registration';
import { ConstructionPhase } from '../models/construction-phase';
import { Message } from '../models/message';
import { Controls } from '../models/controls';
import { WorkerCosts } from '../models/worker-costs';
import { Material } from '../models/material';
import { WorkerDailyHours } from '../models/worker-daily-hours';
import { MaxHoursExceededError, MaxHoursExceededDetail } from '../models/max-hours-exceeded-error';
import { RestApiService } from './rest-api.service';

interface ConstructionSiteDTO {
  TenantID : number;
  ConstructionSiteCode: string;
  ConstructionSiteDescription: string;
  IsPhase: boolean;
  Kilometres: number;
  }

@Injectable({
  providedIn: 'root'
})
export class WorkerAndToolService {

  conPH: ConstructionPhase[] = [];

  constructor(private restApi: RestApiService) {}

  recreateDB() {
    return db.delete().then (()=>db.open());
  }

  getCosts(): Promise<Cost[]> {
    return db.cost.toArray();
  }

  getCostsObservable(): Observable<Cost[]> {
    return from(liveQuery(() => db.cost.toArray()));
  }

  getCostByKey(key: string): Promise<Cost | undefined> {
    return db.cost.get(key);
  }

  async syncCosts(nuoviCosti: Cost[]): Promise<void> {
    await db.transaction('rw', db.cost, async () => {
      await db.cost.clear();
      await db.cost.bulkPut(nuoviCosti);
    });
  }

  async syncCostsAll(nuoviCostiAll: WorkerCosts[]): Promise<void> {
    await db.transaction('rw', db.workerCosts, async () => {
      await db.workerCosts.clear();
      await db.workerCosts.bulkPut(nuoviCostiAll);
    });
  }

  getConstructionSites(): Promise<Constructionsite[]>{
    return db.constructionSite.toArray();
  }

  getConstructionSitesCount(): Promise<number> {
    return db.constructionSite.count();
  }

  getConstructionSitesObservable(): Observable<Constructionsite[]> {
    return from(liveQuery(() => db.constructionSite.toArray()));
  }

  async syncConstructionSites(nuovi: Constructionsite[]): Promise<void> {
    await db.transaction('rw', db.constructionSite, async () => {
      await db.constructionSite.clear();
      await db.constructionSite.bulkPut(nuovi);
    });
  }

  getConstructionSiteByKey(key: string): Promise<Constructionsite | undefined> {
    return db.constructionSite.get(key);
  }

  getConstructionPhases(constructionSite?: string): Promise<ConstructionPhase[]>{
    if (typeof constructionSite !== 'undefined') {
      return db.constructionPhase.where("constructionSiteCode").equalsIgnoreCase(constructionSite).toArray();
    }

    return db.constructionPhase.toArray();
  }

  getConstructionPhasesObservable(): Observable<ConstructionPhase[]> {
    return from(liveQuery(() => db.constructionPhase.toArray()));
  }

  async syncConstructionPhases(nuovi: ConstructionPhase[]): Promise<void> {
    await db.transaction('rw', db.constructionPhase, async () => {
      await db.constructionPhase.clear();
      await db.constructionPhase.bulkPut(nuovi);
    });
  }

  /*
  async getAllConstructionPhases(registration: Registration): Promise<ConstructionPhase[]> {
    // è molto simile alla funzione sopra di  getConstructionPhases() 
    const allPhases: ConstructionPhase[] = [];
    (await db.constructionPhase.toArray()).filter(x => x.constructionSiteCode == registration.cantiere).forEach(x => allPhases.push(x));
    return allPhases;
  }*/

  getTools(): Promise<Tool[]>{
    return db.tool.toArray();
  }

  getToolsObservable(): Observable<Tool[]> {
    return from(liveQuery(() => db.tool.toArray()));
  }


  getMaterials(): Promise<Material[]> {
    return db.materials.toArray();
  }

  getMaterialsObservable(): Observable<Material[]> {
    return from(liveQuery(() => db.materials.toArray()));
  }

  async getMaterial(materialCode: string): Promise<Material | undefined>{
    if (!materialCode) return undefined;
    return await db.materials.get(materialCode);
  }

  async syncTools(nuovi: Tool[]): Promise<void> {
    await db.transaction('rw', db.tool, async () => {
      const count = await db.tool.count();
      if (count > 0) await db.tool.clear();
      await db.tool.bulkPut(nuovi);
    });
  }

  async syncMaterials(nuovi: Material[]): Promise<void> {
    await db.transaction('rw', db.materials, async () => {
      const count = await db.materials.count();
      if (count > 0) await db.materials.clear();
      await db.materials.bulkPut(nuovi);
    });
  }

  async syncWorkerDailyHours(data: WorkerDailyHours[]): Promise<void> {
    await db.transaction('rw', db.workerDailyHours, async () => {
      await db.workerDailyHours.clear();
      await db.workerDailyHours.bulkPut(data);
    });
  }

  async getWorkerDailyHoursForDate(date: string): Promise<WorkerDailyHours[]> {
    return db.workerDailyHours.where('date').equals(date).toArray();
  }

  async getUnsyncedWorkerHoursForDate(date: string, excludeRegistrationId?: number): Promise<Map<string, number>> {
    const registrations = await db.registration.toArray();
    const hoursMap = new Map<string, number>();
    registrations
      .filter(r => r.dataRegistrazioneString === date && r.id !== excludeRegistrationId)
      .forEach(r => {
        r.workerRegistrations.forEach(wr => {
          const current = hoursMap.get(wr.workerNumber) ?? 0;
          hoursMap.set(wr.workerNumber, current + wr.oreLavorate);
        });
      });
    return hoursMap;
  }
  


  async syncControls(nuovi: Controls[]): Promise<void> {
    await db.transaction('rw', db.controls, async () => {
      const count = await db.controls.count();
      if (count > 0) await db.controls.clear();
      await db.controls.bulkPut(nuovi);
    });
  }


  getKindOfWorks(): Promise<KindOfWork[]>{
    return  db.kindOfWork.toArray();
  }

  getKindOfWorksObservable(): Observable<KindOfWork[]> {
    return from(liveQuery(() => db.kindOfWork.toArray()));
  }


  getcostObservable(): Observable<WorkerCosts[]> {
    return from(liveQuery(() => db.workerCosts.toArray()));
  }


  async syncKindOfWorks(nuovi: KindOfWork[]): Promise<void> {
    await db.transaction('rw', db.kindOfWork, async () => {
      await db.kindOfWork.clear();
      await db.kindOfWork.bulkPut(nuovi);
    });
  }

  getWorkers(): Promise<Worker[]>{
    return db.worker.toArray();
  }

  getCurrentWorker(): Promise<Worker | undefined> {
    let user = localStorage.getItem("user");
    if (user == null) user = "   ";
    const workerNumber = user.substring(3);
    return db.worker.get(workerNumber);

  }

  getCurrentWorkerObservable(): Observable<Worker | undefined> {
    let user = localStorage.getItem("user");
    if(user == null) user = "   ";
    const workerNumber = user.substring(3);
    return from(liveQuery(() => db.worker.get(workerNumber)));
  }

  getWorkersObservable(): Observable<Worker[]> {
    return from(liveQuery(() => db.worker.toArray()));
  }

  async syncWorkers(nuovi: Worker[]): Promise<void> {
    await db.transaction('rw', db.worker, async () => {
      await db.worker.clear();
      await db.worker.bulkPut(nuovi);
    });
  }

  getHoursOption(): Promise<HoursOption | undefined> {
    return db.option.toCollection().first();
  }

  getHoursOptionObservable() : Observable<HoursOption | undefined>{
    return from ( liveQuery (()=>db.option.toCollection().first()) );
  }

  async syncHoursOption(nuovi: HoursOption): Promise<void> {
    await db.option.clear();
    await db.option.add(nuovi);
  }

  getMessages(): Promise<Message[]> {
    return db.message.toArray();
  }

  getLastMessage(): Promise<Message | undefined> {
    return db.message.orderBy(":id").last();
  } 

  getMessagesObservable(): Observable<Message[]> {
    return from(liveQuery(() => db.message.toArray()));
  }

  async syncMessages(nuovi: Message[]) {
    await db.message.bulkPut(nuovi);
  }

  getReadedMessageObservable(): Observable<Message[]> {
    return from(liveQuery(() => db.readedMessage.toArray()));
  }

  deleteReaderMessage(messageId: number): Promise<void> {
    return db.readedMessage.delete(messageId);
  }

  async getMessage(messageId: number): Promise<Message | undefined> {
    const message = await db.message.get(messageId);

    if (message) {
      if (!message.flagRead) {
        message.flagRead = true;
        await db.message.update(message.messageId, { flagRead: true })
        const readed = await db.readedMessage.get(messageId);
        if (!readed) {
          await db.readedMessage.add(message);
        }
      }
    }

    return message;
  }

async getRegistrations() : Promise<Registration[]> {
  return await db.registration.toArray();
}

  async getRegistrationsByResource(): Promise<ResourceRegistration[]> {
    const registrations = await db.registration.toArray();

    const result: ResourceRegistration[] = [];

    registrations.forEach(r => {
      r.workerRegistrations.forEach(w => {
        result.push({
          dataRegistrazione: r.dataRegistrazione,
          descrizioneCantiere: r.descrizioneCantiere ?? "",
          id: r.id,
          ResourceDescription: w.workerDescription,
          oreLavorateManodopera: w.oreLavorate,
          oreLavorateMezzi: 0
        });

        r.toolRegistrations.forEach(t => {
          result.push({
            dataRegistrazione: r.dataRegistrazione,
            descrizioneCantiere: r.descrizioneCantiere ?? "",
            id: r.id,
            ResourceDescription: t.toolDescription,
            oreLavorateManodopera: 0,
            oreLavorateMezzi: t.oreLavorate
          });
        });
      });
    });

    return result;
  }

  async getRegistration(registrationId: number) : Promise<Registration | undefined> {
    return await db.registration.get(registrationId);
  }

  async addRegistration(registration: Registration): Promise<number> {
    registration.id = undefined;
    await this.normalizeRegistration(registration);
    await this.validateMaxRegistrationHours(registration);
    return  await db.registration.add(registration)
  }

  async editRegistration(registration: Registration, registrationId: number): Promise<number> {
    await this.normalizeRegistration(registration);
    await this.validateMaxRegistrationHours(registration, registrationId);

    return await db.registration.put(registration, registrationId)
  }

  async validateMaxRegistrationHours(registration: Registration, excludeRegistrationId?: number): Promise<void> {
    const option = await db.option.get(registration.tenantId);
    const maxRegistrationHours = option?.maxRegistrationHours ?? 0;
    if (!maxRegistrationHours || maxRegistrationHours <= 0) return;
    if (!registration.workerRegistrations || registration.workerRegistrations.length === 0) return;

    const date = registration.dataRegistrazioneString;

    if (navigator.onLine) {
      try {
        const fresh = await firstValueFrom(this.restApi.getWorkerDailyHours());
        await this.syncWorkerDailyHours(fresh);
      } catch {
        // offline o errore rete: uso la cache locale
      }
    }

    const [syncedHours, unsyncedHours] = await Promise.all([
      this.getWorkerDailyHoursForDate(date),
      this.getUnsyncedWorkerHoursForDate(date, excludeRegistrationId)
    ]);

    const previousByWorker = new Map<string, number>();
    syncedHours.forEach(h => previousByWorker.set(h.workerNumber, h.totalHours));
    unsyncedHours.forEach((hours, workerNumber) => {
      const current = previousByWorker.get(workerNumber) ?? 0;
      previousByWorker.set(workerNumber, current + hours);
    });

    const newByWorker = new Map<string, number>();
    const descriptionByWorker = new Map<string, string>();
    registration.workerRegistrations.forEach(wr => {
      if (!wr.oreLavorate || wr.oreLavorate <= 0) return;
      const current = newByWorker.get(wr.workerNumber) ?? 0;
      newByWorker.set(wr.workerNumber, current + wr.oreLavorate);
      if (wr.workerDescription) descriptionByWorker.set(wr.workerNumber, wr.workerDescription);
    });

    const overLimit: MaxHoursExceededDetail[] = [];
    newByWorker.forEach((newHours, workerNumber) => {
      const previousHours = previousByWorker.get(workerNumber) ?? 0;
      if (previousHours + newHours > maxRegistrationHours) {
        overLimit.push({
          workerNumber,
          workerDescription: descriptionByWorker.get(workerNumber) ?? workerNumber,
          previousHours,
          newHours
        });
      }
    });

    if (overLimit.length > 0) {
      throw new MaxHoursExceededError(maxRegistrationHours, overLimit);
    }
  }

  showSaveError(err: unknown): boolean {
    if (err instanceof MaxHoursExceededError) {
      const lines = err.overLimitWorkers.map(w =>
        `${w.workerDescription}: ${w.previousHours} + ${w.newHours} = ${w.previousHours + w.newHours} (max: ${err.maxRegistrationHours})`
      );
      alert('I seguenti dipendenti superano il limite di ore giornaliere:<br>' + lines.join('<br>'), 'Errore');
      return true;
    }
    return false;
  }

  async deleteRegistration(registrationId: number): Promise<void> {
    return await db.registration.delete(registrationId);
  }

  async getTempRegistration(registrationId: number): Promise<Registration | undefined> {
    return await db.tempRegistration.get(registrationId);
  }

  async addTempRegistration(registration: Registration): Promise<number> {
    await this.normalizeRegistration(registration);
    await db.tempRegistration.clear();
    if (registration.id == null) registration.id = -1;
    return await db.tempRegistration.add(registration)
  }

  async editTempRegistration(registration: Registration, registrationId: number): Promise<number> {
    await this.normalizeRegistration(registration);
    return await db.tempRegistration.put(registration, registrationId)
  }

  private async normalizeRegistration(registration: Registration) {
    registration.dataRegistrazione = new Date(registration.dataRegistrazioneString);

    let user = localStorage.getItem("user");
    user ??= "   ";
    registration.registrationUser =  user.substring(3);

    registration.uuid = this.getApplicationToken();

    if (registration.cantiere) {
      const cantiere = await db.constructionSite.get(registration.cantiere);
      if (cantiere)
        registration.descrizioneCantiere = cantiere.constructionSiteDescription;
    }
    else {
      registration.descrizioneCantiere = "";
    }

    if (registration.fase) {
      const fase = await db.constructionPhase.get(registration.fase);
      if (fase)
        registration.descrizioneFase = fase.constructionPhaseDescription;
    }
    else {
      registration.descrizioneFase = "";
    }

    if (registration.costo) {
      const costo = await db.cost.get(registration.costo);
      if (costo)
        registration.descrizioneCosto = costo.costDescription;
    }
    else {
      registration.descrizioneCosto = "";
    }

    registration.totaleOreManodopera = registration.workerRegistrations.reduce((total, x) => total + (x.oreLavorate), 0)
    registration.totaleOreMezzi = registration.toolRegistrations.reduce((total, x) => total + (x.oreLavorate), 0)


    // sistemazione dati in caso di fasi multiple
    const options = await db.option.get(registration.tenantId);

    if (options?.multiplePhases) {

      let NoPhase = false;

      if (registration.fase?.length == 0 || registration.noPhase) {
        NoPhase = true;
      }

      if (NoPhase) {

        registration.workerRegistrations.forEach(x => {
          registration.phasesCodesArray?.push(x.phaseCode!);
        });

        // rimuovo i duplicati
        registration.phasesCodesArray = Array.from(new Set(registration.phasesCodesArray));

        // inizializzo l'array delle fasi selezionate ( con tutti i dati )
        registration.allSelectedPhases = [];

        // recupero l'ora massima lavorata per quella fase
        let maxHour = 0;
        //registration.workerRegistrations.filter(x => x.phaseCode == "000").forEach(x => {
        registration.workerRegistrations.filter(x => x.phaseCode == registration.cantiere).forEach(x => {
          if (x.oreLavorate > maxHour) {
            maxHour = x.oreLavorate;
          }
        })

        // compilo i dati 
        if (registration.allSelectedPhases) {
          registration.allSelectedPhases.push({
            constructionPhaseCode: registration.cantiere!,//"000",
            constructionPhaseDescription: registration.descrizioneCantiere!,
            constructionSiteCode: registration.cantiere!,
            hoursTime: maxHour,
            selected: true,
          });
        }

      }
      else {





        if (!registration.phasesCodesArray) {

          const fase = await db.constructionPhase.toArray();

          this.conPH = [];
          fase.forEach(item => {
            this.conPH.push({
              constructionPhaseCode: item.constructionPhaseCode,
              constructionPhaseDescription: item.constructionPhaseDescription,
              constructionSiteCode: item.constructionSiteCode
            });
          });

          // inizializzo l'array delle fasi selezionate ( solo codici )
          registration.phasesCodesArray = [];

          registration.workerRegistrations.forEach(x => {
            registration.phasesCodesArray?.push(x.phaseCode!);
          });

          // rimuovo i duplicati
          registration.phasesCodesArray = Array.from(new Set(registration.phasesCodesArray));

          // inizializzo l'array delle fasi selezionate ( con tutti i dati )
          registration.allSelectedPhases = [];

          registration.phasesCodesArray.forEach(pahseCode => {

            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            const descr = this.conPH.find(x => x.constructionPhaseCode == pahseCode)?.constructionPhaseDescription!;

            // recupero l'ora massima lavorata per quella fase
            let maxHour = 0;
            registration.workerRegistrations.filter(x => x.phaseCode == pahseCode).forEach(x => {
              if (x.oreLavorate > maxHour) {
                maxHour = x.oreLavorate;
              }
            })

            // compilo i dati 
            if (registration.allSelectedPhases) {
              registration.allSelectedPhases.push({
                constructionPhaseCode: pahseCode,
                constructionPhaseDescription: descr,
                constructionSiteCode: "",
                hoursTime: maxHour,
                selected: true,
              });
            }

          });
        }



      }
    }



  }

  async getSelectedWorkerRegistrations(workerRegistrations: WorkerRegistration[]) : Promise<SelectedWorkerRegistration[]> {
    const workers = await db.worker.toArray();
    const result: SelectedWorkerRegistration[] = [];

    for(let i=0;i<workers.length ;i++)
    {
      const selected = workerRegistrations.filter(f=>f.workerNumber === workers[i].workerNumber).length > 0;

      const wholeWorker = workerRegistrations.filter(f => f.workerNumber === workers[i].workerNumber)[0];
      let kCost = "";
      if (wholeWorker != null && wholeWorker.cost != null) {
        kCost = wholeWorker.cost;
      }

      result.push({
        selected: selected,
        workerNumber: workers[i].workerNumber,
        workerDescription: workers[i].surname + ' ' + workers[i].name,
        cost: kCost
      });
    }

    return result;
  }

  async getSelectedToolRegistrations(registration: Registration) : Promise<SelectedToolRegistration[]> {
    const tools = await db.tool.toArray();
    const option = await this.getHoursOption();
    const toolRegistrationsEmpty = option?.toolRegistrationsEmpty ?? false;

    const result: SelectedToolRegistration[] = [];

    for(let i=0;i<tools.length ;i++)
    {
      const selectedTools = registration.toolRegistrations.filter(f => f.toolCode === tools[i].toolCode);
      let selected = selectedTools.length > 0;
      let selectedKindOkWork = null;
      if (selected) {
        selectedKindOkWork = selectedTools[0].kindOfWork;
      }

      if (!selected && !toolRegistrationsEmpty) {
        selected = registration.workerRegistrations.filter(f => f.toolCode === tools[i].toolCode).length > 0;
      }

      result.push({
        selected: selected,
        toolCode: tools[i].toolCode,
        toolDescription: tools[i].toolCode + ' - ' + tools[i].toolDescription,
        toolCategory: tools[i].toolCategory,
        kindOfWork: selectedKindOkWork
      });
    }

    return result;
  }

  async getSelectedControlsRegistrations(registration: Registration): Promise<ControlRegistration[]> {
    const controls = await db.controls.toArray();
    const option = await this.getHoursOption();

    const result: ControlRegistration[] = [];
    const cantiere = registration.cantiere;

    // tengo soltanto quelle righe dei controlli che hanno cantiere corretto oppure vuoto 
    let filtered = controls.filter(x => x.constructionSite == cantiere);

    if (filtered.length == 0) {
      filtered = controls.filter(x => x.constructionSite == "" || x.constructionSite == null)
    }

    for (let i = 0; i < filtered.length; i++) {
      const selectedControl = filtered[i];

      
      result.push({
        selectedPos: false,
        selectedNeg: false,
        controlID: selectedControl.controlId,
        sortingOrder: selectedControl.sortingOrder,
        constructionSite: selectedControl.constructionSite,
        description: selectedControl.description 
      });

    }

    return result;
  }

  async getHasControls(registration: Registration): Promise<boolean> {
    // numero di controlli relativi a "cantieri generici" ( nel caso non ce ne fossero per il cantiere specifico )
    const countControlsEmpty = await db.controls
      .filter(x => x.constructionSite == "" || x.constructionSite == null)
      .count();

    // numero di controlli relativi al cantiere specifico di interesse attuale
    const countControlsTMP = await db.controls
      .filter(x => x.constructionSite == registration?.cantiere)
      .count();

    return countControlsEmpty > 0 || countControlsTMP > 0
  }

  getNewRegistration(): Registration {
    let dataRegistrazione = new Date();
    const offset = dataRegistrazione.getTimezoneOffset();
    dataRegistrazione = new Date(dataRegistrazione.getTime() - (offset * 60 * 1000))
    
    let tenantId = localStorage.getItem("tenantId");
    tenantId ??= "0";

    return {
      tenantId: +tenantId,
      dataRegistrazione: dataRegistrazione,
      dataRegistrazioneString: dataRegistrazione.toISOString().split('T')[0],
      cantiere: null,
      costo: null,
      fase: null,
      oreLavorate: null,
      inizio: null,
      fine: null,
      uuid: null,
      registrationUser: null,
      notes: "",
      workerRegistrations: [],
      toolRegistrations: [],
      workerKilometers: [],
      controlsRegistration: [], // added
      attachments: [],
      descrizioneCantiere: "",
      descrizioneCosto: "",
      descrizioneFase: "",
      totaleOreManodopera: 0,
      totaleOreMezzi: 0,
      noteControlli: "", // added

      notes2: "",
      notes3: "",
      notes4: "",

      allPhases: [],
      allSelectedPhases: [],
      phasesCodesArray: [],
      noPhase: false,

      signatureData: null, // added
      warehouseArticles: [],
    };
  }

  getApplicationToken(): string {
    let uuid = localStorage.getItem("uuid");
    if (!uuid) {
      uuid = this.getUniqueId(4);
      localStorage.setItem("uuid", uuid);
    }

    return uuid;
  }

  getNotificationToken(): string {
    return localStorage.getItem("notificationToken") ?? "";
  }

  getUniqueId(parts: number): string {
    const stringArr = [];
    for (let i = 0; i < parts; i++) {
      // tslint:disable-next-line:no-bitwise
      const S4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
      stringArr.push(S4);
    }
    return stringArr.join('-');
  }

  exportDatabase(): string {
    const allData =  db.transaction('r', db.tables, () => {
      return Promise.all(
        db.tables.map(table => table.toArray()
          .then(rows => ({ table: table.name, rows: rows }))));
    });

    const serialized = JSON.stringify(allData);

    return serialized;
  }
}
